import { useEffect, useState } from "react";
import { api, eur, dateFr, API_BASE } from "@/lib/rentApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, FileDown, Send, Bell } from "lucide-react";

export default function RentPayments() {
  const { user } = useAuth();
  const isOwner = user.role === "proprietaire" || user.role === "admin";
  const isTenant = user.role === "locataire";
  const [items, setItems] = useState(null);
  const [paying, setPaying] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const load = () => api.get("/rent-payments").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const markPaid = async (id) => { await api.put(`/rent-payments/${id}/mark-paid`); toast.success("Loyer marqué payé — quittance générée"); load(); };

  const downloadQuittance = async (p) => {
    try {
      const token = localStorage.getItem("rent_token");
      const res = await fetch(`${API_BASE}/rent-payments/${p.id}/quittance`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `quittance_${p.period.replace("/", "-")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Quittance indisponible"); }
  };

  const sendQuittance = async (p) => {
    try {
      const { data } = await api.post(`/rent-payments/${p.id}/quittance/send`);
      toast.success(data.sent ? `Quittance envoyée à ${data.email}` : "Aucun email locataire renseigné");
    } catch { toast.error("Envoi impossible"); }
  };

  const remindOne = async (p) => {
    try { const { data } = await api.post(`/rent-payments/${p.id}/remind`); toast.success(data.sent ? "Relance envoyée au locataire" : "Aucun email locataire renseigné"); }
    catch { toast.error("Relance impossible"); }
  };
  const remindAll = async () => {
    try { const { data } = await api.post("/rent-payments/reminders/run"); toast.success(`${data.sent} relance(s) envoyée(s)`); }
    catch { toast.error("Erreur lors des relances"); }
  };

  const payOnline = async (p) => {
    setPaying(p.id);
    try {
      const { data } = await api.post("/payments/checkout", { payment_id: p.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Paiement indisponible"); setPaying(null); }
  };

  if (!items) return <Loading />;
  const totalDue = items.filter((p) => p.status !== "payé").reduce((s, p) => s + p.amount, 0);

  return (
    <div>
      <PageHeader
        title={isTenant ? "Mes loyers" : "Loyers"}
        subtitle={isTenant ? "Réglez vos loyers en ligne par carte bancaire." : "Suivi des encaissements de loyers."}
        action={isOwner && <Button variant="outline" className="rounded-full gap-2" onClick={remindAll} data-testid="remind-all-btn"><Bell className="w-4 h-4" /> Relancer les impayés</Button>}
      />
      <div className="mb-6 p-5 rounded-xl border border-border bg-card inline-flex items-center gap-6">
        <div><p className="text-sm text-muted-foreground">Total dû</p><p className="font-heading text-2xl font-bold" data-testid="total-due">{eur(totalDue)}</p></div>
      </div>

      {items.length === 0 ? (
        <EmptyState title="Aucun loyer" desc={isOwner ? "Créez un bail pour générer l'échéancier." : "Aucun loyer à régler."} />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Période</TableHead>{isOwner && <TableHead>Locataire</TableHead>}
                <TableHead>Échéance</TableHead><TableHead>Montant</TableHead>
                <TableHead>Statut</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                  <TableCell className="font-mono-plex">{p.period}</TableCell>
                  {isOwner && <TableCell>{p.tenant_name}</TableCell>}
                  <TableCell>{dateFr(p.due_date)}</TableCell>
                  <TableCell className="font-medium">{eur(p.amount)}</TableCell>
                  <TableCell>
                    <Badge variant={p.status === "payé" ? "default" : "secondary"} className={p.status === "payé" ? "bg-[hsl(var(--chart-2))]" : ""}>{p.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {p.status === "payé" ? (
                      <div className="inline-flex items-center gap-2 justify-end">
                        <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => downloadQuittance(p)} data-testid={`quittance-btn-${p.id}`}><FileDown className="w-4 h-4" /> Quittance</Button>
                        {isOwner && <Button size="sm" variant="ghost" className="rounded-full gap-1" onClick={() => sendQuittance(p)} data-testid={`send-quittance-btn-${p.id}`}><Send className="w-4 h-4" /> Envoyer</Button>}
                      </div>
                    ) : isTenant ? (
                      <Button size="sm" className="rounded-full gap-1" disabled={paying === p.id} onClick={() => payOnline(p)} data-testid={`pay-btn-${p.id}`}>
                        <CreditCard className="w-4 h-4" /> {paying === p.id ? "…" : "Payer"}
                      </Button>
                    ) : (
                      <div className="inline-flex gap-2 justify-end">
                        {p.due_date < today && <Button size="sm" variant="ghost" className="rounded-full gap-1 text-[hsl(var(--chart-3))]" onClick={() => remindOne(p)} data-testid={`remind-btn-${p.id}`}><Bell className="w-4 h-4" /> Relancer</Button>}
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => markPaid(p.id)} data-testid={`markpaid-btn-${p.id}`}>Marquer payé</Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
